import React from 'react';
import { notifications } from '@mantine/notifications';
import { IconAlertCircle } from '@tabler/icons-react';

const recentNotifications = new Map<string, number>();
const COOLDOWN_MS = 4000;

export function formatUserErrorMessage(error: unknown, fallbackMessage: string): string {
  if (!error && !fallbackMessage) {
    return 'An unexpected error occurred. Please try again.';
  }

  let rawMessage = '';
  if (typeof error === 'string') {
    rawMessage = error;
  } else if (error && typeof error === 'object') {
    if ('message' in error && typeof (error as { message: unknown }).message === 'string') {
      rawMessage = (error as { message: string }).message;
    } else if ('error' in error && typeof (error as { error: unknown }).error === 'string') {
      rawMessage = (error as { error: string }).error;
    }
  }

  if (!rawMessage) {
    return fallbackMessage;
  }

  const isTechnical = /https?:\/\/|:\d+|\b(?:error|exception|failed|status|econn)\b/i.test(rawMessage);
  if (isTechnical || rawMessage.length > 100) {
    return fallbackMessage || 'A network or server communication error occurred. Please try again later.';
  }

  return rawMessage;
}

export interface ShowErrorNotificationOptions {
  id?: string;
  title?: string;
  message: string;
  error?: unknown;
  autoClose?: number | boolean;
}

export function showErrorNotification({
  id = 'global-error',
  title = 'Something went wrong',
  message,
  error,
  autoClose = 4000,
}: ShowErrorNotificationOptions): void {
  if (
    error &&
    typeof error === 'object' &&
    'name' in error &&
    (error as { name: string }).name === 'AbortError'
  ) {
    return;
  }

  const now = Date.now();
  const lastShown = recentNotifications.get(id);
  if (lastShown && now - lastShown < COOLDOWN_MS) {
    return;
  }
  recentNotifications.set(id, now);

  const userFriendlyMessage = formatUserErrorMessage(error, message);

  notifications.show({
    id,
    color: 'red',
    icon: React.createElement(IconAlertCircle, { size: 18 }),
    title,
    message: userFriendlyMessage,
    autoClose,
  });
}
